import React, { ReactNode } from 'react';
import { CCol, CRow } from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilSearch } from '@coreui/icons';
import FilterIconMenu from './FilterIconMenu';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'; // ← NEW for Word
import { Tooltip } from '@mui/material';
import CustomSelectInput from '../InputsComponent/CustomSelectInput';
import Spinner from '../UI/Spinner';

interface OtherControl {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: any;
  fn: () => void;
  title?: string;
}

interface DataGridHeaderProps {
  title: string;
  addButton?: string;
  addBtnClick?: () => void;
  otherControls?: OtherControl[];
  titleControls?: OtherControl[];
  addSecButton?: string;
  addSecBtnClick?: () => void;
  filterDisable?: boolean;
  wordLoading?: boolean;
  exportFn?: () => void;           // PDF export
  wordExportFn?: () => void;       // ← NEW: Word export
  addBtnContent?: ReactNode;
  popOverId?: string;
  headerClassName?: string;
  actionCell?: ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  actions?: any[];
  onTitleClick?: () => void;
  topSearch?: {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    placeholder?: string;
  };
  topSelect?: {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data?: any[];
  };
}

const DataGridHeader: React.FC<DataGridHeaderProps> = ({
  title,
  addButton,
  addBtnClick,
  otherControls,
  addSecButton,
  addSecBtnClick,
  filterDisable,
  exportFn,
  wordExportFn,           // ← NEW prop
  addBtnContent,
  popOverId,
  actionCell,
  actions,
  titleControls,
  onTitleClick,
  topSearch,
  wordLoading,
  topSelect,
  headerClassName,
}) => {
  return (
    <div className={`d-flex flex-column w-100 ${headerClassName || ''}`}>
      <CRow className="w-100 p-0 align-self-center g-2">
        {/* Left Section */}
        <CCol xs={12} lg={3} className="d-flex justify-content-start align-items-center p-0">
          <div className="pointer heading-font-weight" onClick={onTitleClick}>
            {title}
          </div>
          {titleControls && <span className="mx-2">|</span>}
          {titleControls &&
            titleControls.map(({ icon, fn, title }, index) => (
              <div key={index} className="d-flex flex-row gap-1 align-items-center ms-2">
                {title && (
                  <p className="labelName heading-font-weight text-truncate m-0" onClick={fn}>
                    {title}
                  </p>
                )}
                <CIcon className="stock-toggle-icon" onClick={fn} icon={icon} />
              </div>
            ))}
        </CCol>

        {/* Center Section - Search & Filter */}
        <CCol xs={12} lg={6} className="p-0">
          <CRow className="g-2 justify-content-center">
            {topSearch && (
              <CCol xs={12} sm={6} className="d-flex justify-content-center">
                <div className="position-relative w-100" style={{ maxWidth: '400px' }}>
                  <input
                    type="text"
                    className="form-control top-search-input"
                    placeholder={topSearch.placeholder || 'Search...'}
                    value={topSearch.value}
                    onChange={topSearch.onChange}
                    style={{
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      paddingRight: '35px',
                    }}
                  />
                  <CIcon
                    icon={cilSearch}
                    className="position-absolute"
                    style={{
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      opacity: 0.6,
                      pointerEvents: 'none',
                    }}
                  />
                </div>
              </CCol>
            )}

            {topSelect && (
              <CCol xs={12} sm={6} className="d-flex justify-content-center">
                <div className="w-100" style={{ maxWidth: '400px' }}>
                  <CustomSelectInput
                    id="advance-select"
                    onChange={topSelect?.onChange}
                    value={topSelect?.value}
                    options={topSelect?.data}
                    className="form-control pe-5 top-search-input"
                    disableOption="Select Advance Filter"
                    width={'w-100'}
                    showDisabled={true}
                    icon={undefined}
                    name={undefined}
                    isRequired={undefined}
                    disabled={undefined}
                    title={undefined}
                    message={undefined}
                    label={undefined}
                    onClick={undefined}
                    optionsList={undefined}
                  />
                </div>
              </CCol>
            )}
          </CRow>
        </CCol>

        {/* Right Section - Actions + Exports */}
        <CCol
          xs={12}
          lg={3}
          className="d-flex justify-content-lg-end justify-content-start align-items-center gap-3 p-0 flex-wrap"
        >
          {actionCell && actionCell}

          {actions &&
            actions.map((action, index) => (
              <span
                key={index}
                className="lblAddPartner labelName heading-font-weight text-truncate"
                onClick={action.onClick}
              >
                {action.title}
              </span>
            ))}

          {addButton && (
            <span
              className="lblAddPartner labelName heading-font-weight text-truncate"
              onClick={addBtnClick}
              aria-describedby={popOverId}
            >
              {addButton} {addBtnContent ? addBtnContent : '(+)'}
            </span>
          )}

          {addSecButton && (
            <span
              className="lblAddPartner labelName heading-font-weight text-truncate"
              onClick={addSecBtnClick}
            >
              {addSecButton}
            </span>
          )}

          {/* PDF Export Icon */}
          {exportFn && (
            <Tooltip title="Download PDF Report">
              <FileDownloadOutlinedIcon
                fontSize="medium"
                className="stock-toggle-icon"
                onClick={exportFn}
              />
            </Tooltip>
          )}

          {/* Word Export Icon - NEW */}
          {wordExportFn && (
            <Tooltip title="Download Word Report (.docx)">
              {wordLoading ? <Spinner/>: <DescriptionOutlinedIcon
                fontSize="medium"
                className="stock-toggle-icon"
                onClick={wordExportFn}
                sx={{ color: '#2b579a' }} // Microsoft Word blue (optional)
              /> }
             
            </Tooltip>
          )}

          {otherControls &&
            otherControls.map(({ icon, fn, title }, index) => (
              <div key={index} className="d-flex flex-row gap-1 align-items-center">
                {title && (
                  <p
                    className="lblAddPartner labelName heading-font-weight text-truncate m-0"
                    onClick={fn}
                  >
                    {title}
                  </p>
                )}
                <CIcon className="stock-toggle-icon" onClick={fn} icon={icon} />
              </div>
            ))}

          {!filterDisable && <FilterIconMenu />}
        </CCol>
      </CRow>
    </div>
  );
};

export default DataGridHeader;
